import { LoadingButton } from '@mui/lab';
import {
    Box,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import SkeletonTable, { SkeletonColumnConfig } from 'components/shared/skeleton/skeleton-table.component';
import { useEditLab } from 'context/edit-lab-context';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppToasterContext } from '../../../shared/app-toaster/app-toaster-context';
import { customerSettings } from 'utils/customerSettings';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { CONSTANTS } from 'config/constants';
import CopyPriceListDialog from './copy-price-list-dialog';
import { useLazyQuery } from '@apollo/client';
import { labsForCopyPriceListTemplate } from 'apollo/templates/organization';
import { Chip } from '@mui/material';
import PriceHistoryButton from 'components/price-requests/history/price-history-button.component';

const skeletonColumns: SkeletonColumnConfig[] = [
    { type: 'button', width: 80 },
    { type: 'text', width: 100 },
    { type: 'text', width: 100 },
    { type: 'text', width: 100 },
    { type: 'text', width: 100 },
];

const PriceListActions: FC = () => {
    const { t: translate } = useTranslation(['common', 'actions-materials', 'client-directory']);
    const {
        lab,
        priceList,
        isPriceListLoading,
        isAlreadySetCategory,
        updatePriceList,
        deletePriceList,
        activeActionSet,
        selectedCategory,
        updatePriceListLoading,
        deletePriceListLoading,
    } = useEditLab();
    const currentOrg = currentOrgVar();

    const { setAppToasterContent } = useContext(AppToasterContext);
    const currentPriceList = priceList[0];

    const [labRanking, setLabRanking] = useState<Record<string, string>>({});
    const [actionChanges, setActionChanges] = useState<Record<string, any>>({});
    const [rankingTouched, setRankingTouched] = useState(false);
    const [touchedActions, setTouchedActions] = useState<Record<string, boolean>>({});
    const [copyPricelistFrom, setCopyPricelistFrom] = useState(false);
    const [selectedLabId, setSelectedLabId] = useState('');
    const [dialog, setDialog] = useState(false);
    const [availableLabs, setAvailableLabs] = useState<Array<{ _id: string; name: string; priceList: any[] }>>([]);

    const [fetchAvailableLabs, { loading: availableLabsLoading }] = useLazyQuery(labsForCopyPriceListTemplate, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            if (data?.labsForCopyPriceList) {
                setAvailableLabs(data.labsForCopyPriceList);
            }
        },
    });

    const showToast = (severity: 'success' | 'error', message: string) =>
        setAppToasterContent({ isOpen: true, severity, message });

    const isTioTandvardPricingEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.ENABLE_TIOTANDVARD_PRICING,
    );

    useEffect(() => {
        setActionChanges({});
        setLabRanking((prev) => {
            const updated = { ...prev };
            if (currentPriceList?.rank) {
                updated[selectedCategory] = currentPriceList.rank.toString();
            } else {
                delete updated[selectedCategory];
            }
            return updated;
        });
        setRankingTouched(false);
        setTouchedActions({});
        setCopyPricelistFrom(false);
        setSelectedLabId('');
    }, [selectedCategory, currentPriceList?.rank]);

    const currentLabRanking = labRanking[selectedCategory];

    const actions = useMemo(() => {
        return currentPriceList?.actions?.map((a) => ({ ...a, ...actionChanges[a.action] })) || [];
    }, [currentPriceList?.actions, actionChanges]);

    const handleCheckboxChange = (id: string) => {
        const a = actions.find((x) => x.action === id);
        setActionChanges((prev) => ({
            ...prev,
            [id]: { enable: !a?.enable, price: '', deliveryTime: '', tiotandvardPrice: '' },
        }));
    };

    const handleTextboxChange = (actionId: string, field: string, value: string) => {
        setTouchedActions((prev) => ({ ...prev, [actionId]: true }));

        setActionChanges((prev) => ({
            ...prev,
            [actionId]: { ...prev[actionId], [field]: value },
        }));
    };

    const isFormValid = useMemo(() => {
        const enabled = actions.filter((a) => a.enable);
        return (
            enabled.length > 0 &&
            enabled.every((a) => {
                const priceValid = a.actionObj?.quotable || a.price;
                return priceValid && a.deliveryTime;
            })
        );
    }, [actions]);

    const hasDataChanged = useMemo(() => {
        const newRank = Number(currentLabRanking ?? currentPriceList?.rank ?? 0);
        if (newRank !== (currentPriceList?.rank || 0)) return true;

        return actions.some((a) => {
            const orig = currentPriceList?.actions?.find((x) => x.action === a.action);
            return (
                a.enable !== orig?.enable ||
                a.price !== orig?.price ||
                a.tiotandvardPrice !== orig?.tiotandvardPrice ||
                a.deliveryTime !== orig?.deliveryTime
            );
        });
    }, [actions, currentLabRanking, currentPriceList]);

    const handleSave = async () => {
        const rank = Number(currentLabRanking || currentPriceList?.rank);
        if (rank <= 0) return showToast('error', translate('organization:LAB_RANKING_GREATER_THAN_ZERO'));

        const invalid = actions.find((a) => a.enable && Number(a.deliveryTime) <= 0);
        if (invalid) return showToast('error', translate('organization:DELIVERY_TIME_GREATER_THAN_ZERO'));
        await updatePriceList(
            activeActionSet,
            selectedCategory,
            actions.map((a) => ({
                action: a.action,
                enable: a.enable,
                ...(a.enable && {
                    price: a.price ? Number(a.price) : undefined,
                    tiotandvardPrice: Number(a.tiotandvardPrice),
                    deliveryTime: Number(a.deliveryTime),
                }),
            })),
            rank,
        );

        setActionChanges({});
        setLabRanking((prev) => {
            const x = { ...prev };
            delete x[selectedCategory];
            return x;
        });

        showToast('success', translate('organization:PRICE_LIST_UPDATED_SUCCESSFULLY'));
    };

    const handleRemove = async () => {
        await deletePriceList(activeActionSet, selectedCategory);
        setActionChanges({});
        setLabRanking((prev) => {
            const x = { ...prev };
            delete x[selectedCategory];
            return x;
        });
        showToast('success', translate('organization:PRICE_LIST_REMOVED_SUCCESSFULLY'));
    };

    const isRankingInvalid = rankingTouched && (Number(currentLabRanking) <= 0 || isNaN(Number(currentLabRanking)));

    const handleCopyPricelist = (value: string) => {
        setSelectedLabId(value);
        setDialog(true);
    };

    const handleCloseCopyDialog = () => {
        setDialog(false);
        setSelectedLabId('');
    };

    const handleCopyPriceList = () => {
        const selectedLab = availableLabs.find((lab) => lab._id === selectedLabId);
        if (selectedLab?.priceList) {
            const currentActions = currentPriceList?.actions || [];
            const newActionChanges: Record<string, any> = {};

            currentActions.forEach((action: any, index: number) => {
                const sourceItem = selectedLab.priceList[index];
                if (sourceItem) {
                    newActionChanges[action.action] = {
                        enable: sourceItem.enable,
                        price: sourceItem.price ?? '',
                        deliveryTime: sourceItem.deliveryTime ?? '',
                        tiotandvardPrice: sourceItem.tiotandvardPrice ?? '',
                    };
                }
            });

            setActionChanges(newActionChanges);
            showToast('success', translate('organization:PRICE_LIST_COPIED_SUCCESSFULLY'));
        }
        setDialog(false);
    };

    return (
        <>
            {currentPriceList?.actions?.length > 0 && (
                <>
                    <Divider sx={{ mt: 3, mb: 3 }} />

                    <Box>
                        <Box sx={{ mb: 3 }}>
                            <FormControl variant="outlined" size="small" error={isRankingInvalid}>
                                <InputLabel>
                                    {translate('organization:ENTER_LAB_RANKING')}{' '}
                                    <span style={{ color: 'red' }}>*</span>
                                </InputLabel>
                                <OutlinedInput
                                    type="number"
                                    value={currentLabRanking ?? currentPriceList?.rank ?? ''}
                                    onChange={(e) => {
                                        setRankingTouched(true);
                                        setLabRanking((prev) => ({ ...prev, [selectedCategory]: e.target.value }));
                                    }}
                                    label={translate('organization:ENTER_LAB_RANKING') + ' *'}
                                    disabled={updatePriceListLoading}
                                />
                                {rankingTouched && isRankingInvalid && (
                                    <Typography variant="caption" color="error">
                                        {translate('organization:REQUIRED_POSITIVE_NUMBER')}
                                    </Typography>
                                )}
                            </FormControl>
                            <FormControlLabel
                                sx={{ ml: 2 }}
                                control={
                                    <Checkbox
                                        checked={copyPricelistFrom}
                                        onChange={(e) => {
                                            setCopyPricelistFrom(e.target.checked);
                                            if (e.target.checked) {
                                                fetchAvailableLabs({
                                                    variables: {
                                                        categoryId: selectedCategory,
                                                        actionSetId: activeActionSet,
                                                    },
                                                });
                                            } else {
                                                setSelectedLabId('');
                                                setAvailableLabs([]);
                                            }
                                        }}
                                        disabled={updatePriceListLoading}
                                    />
                                }
                                label={translate('organization:COPY_PRICELIST_FROM')}
                            />
                            {copyPricelistFrom && (
                                <FormControl variant="outlined" size="small" sx={{ ml: 2, minWidth: 250 }}>
                                    <InputLabel>{translate('organization:SELECT_LAB')}</InputLabel>
                                    <Select
                                        value={selectedLabId}
                                        onChange={(e: SelectChangeEvent<string>) => handleCopyPricelist(e.target.value)}
                                        label={translate('organization:SELECT_LAB')}
                                        disabled={updatePriceListLoading || availableLabsLoading}
                                    >
                                        <MenuItem value="">
                                            <em>
                                                {availableLabsLoading
                                                    ? translate('organization:LOADING')
                                                    : translate('organization:NO_LABS_FOUND')}
                                            </em>
                                        </MenuItem>
                                        {availableLabs.map((lab) => (
                                            <MenuItem key={lab._id} value={lab._id}>
                                                {lab.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </Box>

                        <TableContainer component={Paper} sx={{ mb: 3 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ backgroundColor: 'background.paper' }} />
                                        <TableCell sx={{ backgroundColor: 'background.paper' }}>
                                            {translate('actions-materials:CODE')}
                                        </TableCell>
                                        <TableCell sx={{ backgroundColor: 'background.paper' }}>
                                            {translate('common:NAME')}
                                        </TableCell>
                                        <TableCell sx={{ backgroundColor: 'background.paper' }}>
                                            {translate('common:QUOTABLE')}
                                        </TableCell>
                                        <TableCell sx={{ backgroundColor: 'background.paper' }}>
                                            {translate('common:PRICE')}
                                        </TableCell>
                                        {isTioTandvardPricingEnabled && (
                                            <TableCell sx={{ backgroundColor: 'background.paper' }}>
                                                {translate('common:TIOTANDVARD_PRICE')}
                                            </TableCell>
                                        )}
                                        <TableCell sx={{ backgroundColor: 'background.paper' }}>
                                            {translate('client-directory:DELIVERY_TIME')}
                                        </TableCell>
                                        <TableCell sx={{ backgroundColor: 'background.paper' }} />
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {isPriceListLoading ? (
                                        <SkeletonTable columns={skeletonColumns} rows={5} renderRowsOnly />
                                    ) : (
                                        actions.map((action) => {
                                            const isDeliveryInvalid =
                                                touchedActions[action.action] &&
                                                (Number(action.deliveryTime) <= 0 ||
                                                    isNaN(Number(action.deliveryTime)));

                                            return (
                                                <TableRow key={action.action}>
                                                    <TableCell sx={{ height: 72, verticalAlign: 'top', paddingTop: 2 }}>
                                                        <Checkbox
                                                            checked={action.enable}
                                                            onChange={() => handleCheckboxChange(action.action)}
                                                            disabled={updatePriceListLoading || deletePriceListLoading}
                                                        />
                                                    </TableCell>

                                                    <TableCell sx={{ height: 72, verticalAlign: 'top', paddingTop: 2 }}>
                                                        {action.actionObj?.number}
                                                    </TableCell>
                                                    <TableCell sx={{ height: 72, verticalAlign: 'top', paddingTop: 2 }}>
                                                        {action.actionObj?.name}
                                                    </TableCell>
                                                    <TableCell sx={{ height: 72, verticalAlign: 'top', paddingTop: 2 }}>
                                                        {action.actionObj?.quotable ? (
                                                            <Chip label={translate(`common:QUOTABLE`)} size="small" />
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </TableCell>

                                                    <TableCell sx={{ height: 72, verticalAlign: 'top' }}>
                                                        <TextField
                                                            size="small"
                                                            type="text"
                                                            value={action.price === 0 ? 0 : action.price || ''}
                                                            onChange={(e) =>
                                                                handleTextboxChange(
                                                                    action.action,
                                                                    'price',
                                                                    e.target.value,
                                                                )
                                                            }
                                                            disabled={!action.enable || updatePriceListLoading}
                                                        />
                                                    </TableCell>

                                                    {isTioTandvardPricingEnabled && (
                                                        <TableCell sx={{ height: 72, verticalAlign: 'top' }}>
                                                            <TextField
                                                                size="small"
                                                                type="text"
                                                                value={action.tiotandvardPrice || ''}
                                                                onChange={(e) =>
                                                                    handleTextboxChange(
                                                                        action.action,
                                                                        'tiotandvardPrice',
                                                                        e.target.value,
                                                                    )
                                                                }
                                                                disabled={!action.enable || updatePriceListLoading}
                                                            />
                                                        </TableCell>
                                                    )}

                                                    <TableCell sx={{ height: 72, verticalAlign: 'top' }}>
                                                        <TextField
                                                            size="small"
                                                            type="text"
                                                            value={action.deliveryTime || ''}
                                                            onChange={(e) =>
                                                                handleTextboxChange(
                                                                    action.action,
                                                                    'deliveryTime',
                                                                    e.target.value,
                                                                )
                                                            }
                                                            disabled={!action.enable || updatePriceListLoading}
                                                            error={action.enable && isDeliveryInvalid}
                                                            helperText={
                                                                action.enable && isDeliveryInvalid
                                                                    ? translate('organization:REQUIRED_POSITIVE_NUMBER')
                                                                    : ''
                                                            }
                                                        />
                                                    </TableCell>

                                                    {/* DD-3697: the product's price timeline with this lab. */}
                                                    <TableCell sx={{ height: 72, verticalAlign: 'top', paddingTop: 2 }}>
                                                        <PriceHistoryButton
                                                            counterpartId={lab._id}
                                                            actionId={action.action}
                                                            actionSetId={activeActionSet}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            {isAlreadySetCategory && (
                                <LoadingButton
                                    variant="outlined"
                                    color="error"
                                    loading={deletePriceListLoading}
                                    disabled={updatePriceListLoading || deletePriceListLoading}
                                    onClick={handleRemove}
                                >
                                    {translate('common:REMOVE')}
                                </LoadingButton>
                            )}

                            <LoadingButton
                                variant="contained"
                                color="primary"
                                loading={updatePriceListLoading}
                                disabled={
                                    updatePriceListLoading ||
                                    Number(currentLabRanking ?? currentPriceList?.rank ?? 0) === 0 ||
                                    !isFormValid ||
                                    !hasDataChanged ||
                                    deletePriceListLoading
                                }
                                onClick={handleSave}
                            >
                                {translate('common:SAVE')}
                            </LoadingButton>
                        </Box>
                    </Box>
                </>
            )}

            {dialog && (
                <CopyPriceListDialog
                    open={dialog}
                    handleClose={handleCloseCopyDialog}
                    handleConfirm={handleCopyPriceList}
                />
            )}
        </>
    );
};

export default PriceListActions;
